import { useState } from 'react';
import {
  COLORS,
  SPACING,
  BORDER_RADIUS,
  INPUT_TOKENS,
  DETAIL_TOKENS,
  SHADOWS,
} from '../../../styles/UISystem';
import PhotoSelectionOverlay from '../../Overlay/PhotoSelectionOverlay';
import useProfilePhotoPicker from '../../../hooks/useProfilePhotoPicker';
import { IdentificationBadge } from '@phosphor-icons/react/dist/ssr/IdentificationBadge';
import { CaretDown } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { User } from '@phosphor-icons/react/dist/ssr/User';
import useAutoGrowTextarea from '../../../hooks/useAutoGrowTextarea';
import ContactFields from './ContactFields';
import CityAutocomplete from './CityAutocomplete';

/**
 * ProfileForm - Shared profile editing form
 *
 * Used by ProfileScreen (all modes: edit, first-time, anon-profile-creation)
 * Features:
 * - Photo selection from predefined images
 * - US phone formatting (xxx) yyy-zzzz
 * - Email validation
 * - Instagram with @ symbol
 * - LinkedIn username
 * - WhatsApp number
 * - Hometown (current city) — its own prominent section; auto-selected in the sharing UI, shared on confirm
 * - About Me info (role, organization, what I do, places lived)
 *
 * @param {Object} profile - Profile data object
 * @param {Function} onProfileChange - Called when any field changes
 * @param {Function} onImageClick - Called when photo is clicked (optional, defaults to photo selector)
 * @param {boolean} hidePhoto - If true, hides the photo section (photo is in header instead)
 * @param {boolean} isCreationFlow - If true, shows reduced starter fields and collapses About Me (for new user creation)
 */
/**
 * AboutField — one About-me row: a persistent grey label above a compact box.
 *
 * The label stays visible once the field is filled (user, 2026-08-20). The
 * earlier design put the field name in the placeholder, which meant identity
 * vanished the moment a value was typed — "Founder" and "Shoshin Labs" read as
 * two unlabelled strings. The contact rows above solve this with icons; these
 * fields have none, so the label carries it.
 *
 * multiline fields grow with their content via useAutoGrowTextarea.
 */
const ABOUT_ME_EXPANDED_KEY = 'profile_about_expanded';

// Same pill shape and height as a compact ContactField row, but deliberately
// WIDER (see DETAIL_TOKENS.ABOUT_ROW_*) — free-text answers need the room, a
// contact handle does not, and the designs carry the same 1.27 ratio.
//
// Definition comes from ELEVATION, not a heavy stroke. A 1px #D1D1D6 border was
// tried first and read as "weird" on device (2026-08-20) — a hard outline around
// an empty box looks like a disabled control. Hometown's treatment (hairline
// border + TIER1 lift) does the same job softly, so every field on this page now
// shares it.
//
// The whole field is a <label>, so any tap inside it focuses the control.
// NOTE: an earlier version of this comment blamed 32px of dead container
// padding for the Org bug. That was wrong — the device readout later showed the
// input was never the problem at all; taps on the field's NAME were the ones
// going nowhere. Kept as a correction so the next reader does not re-derive the
// wrong cause from a stale comment.
const ABOUT_BOX = {
  display: 'flex',
  width: '100%',
  minHeight: '52px',
  // No vertical padding on single-line fields: the input stretches to fill the
  // box, so every pixel inside the border belongs to the control. (This was
  // once believed to be the Org fix; it was not — see the note above.)
  padding: '0 16px',
  borderRadius: INPUT_TOKENS.CONTACT_RADIUS,
  border: INPUT_TOKENS.CONTACT_BORDER,
  backgroundColor: INPUT_TOKENS.CONTACT_BG_EDITABLE,
  boxShadow: SHADOWS.TIER1,
  boxSizing: 'border-box',
  cursor: 'text',
};

const ABOUT_INPUT = {
  flex: 1,
  // Fill the box in BOTH axes. width/minWidth stop the input collapsing to its
  // intrinsic width; alignSelf:stretch + the box's zero vertical padding make it
  // fill the 52px height, so there is no strip of container left to tap.
  width: '100%',
  minWidth: 0,
  alignSelf: 'stretch',
  border: 'none',
  fontSize: '17px',
  color: COLORS.DARK_GRAY,
  backgroundColor: 'transparent',
  outline: 'none',
};

const AboutField = ({
  label,
  value,
  onChange,
  name,
  autoCapitalize = 'words',
  autoCorrect = 'off',
  multiline = false,
}) => {
  const growRef = useAutoGrowTextarea(value, { minHeight: 24, maxHeight: 200 });

  // The field name lives in the PLACEHOLDER, inside the box — restored on the
  // user's call, 2026-08-20, after labels above the box regressed tapping.
  //
  // These fields worked before this branch moved the name OUT of the input and
  // into a sibling <div>. That div became a tap surface that focused nothing,
  // and on device the taps that failed were the ones landing on it. Six rounds
  // of htmlFor, explicit focus handlers, pointer-events and padding changes did
  // not recover it; putting the name back inside the control does, because now
  // there is no part of the field that is not the input.
  //
  // Known trade-off, accepted: identity disappears once a value is typed —
  // "Founder" and "Shoshin Labs" read as two unlabelled strings. Revisit with a
  // floating label only if it can be done without a separate tap surface.
  return (
    <div
      style={{
        marginBottom: '20px',
        scrollMarginTop: '16px',
        width: DETAIL_TOKENS.ABOUT_ROW_WIDTH,
      }}
    >
      <div
        style={{
          ...ABOUT_BOX,
          alignItems: multiline ? 'flex-start' : 'center',
          padding: multiline ? '14px 16px' : ABOUT_BOX.padding,
        }}
      >
        {multiline ? (
          <textarea
            ref={growRef}
            value={value}
            onChange={onChange}
            placeholder={label}
            autoCapitalize="sentences"
            autoCorrect="on"
            rows={1}
            style={{ ...ABOUT_INPUT, resize: 'none', fontFamily: 'inherit' }}
          />
        ) : (
          <input
            type="text"
            name={name}
            value={value}
            onChange={onChange}
            placeholder={label}
            autoCapitalize={autoCapitalize}
            autoCorrect={autoCorrect}
            style={ABOUT_INPUT}
          />
        )}
      </div>
    </div>
  );
};

const ProfileForm = ({
  profile,
  onProfileChange,
  onImageClick,
  hidePhoto = false,
  hideName = false,
  isCreationFlow = false,
}) => {
  const [showPhotoSelector, setShowPhotoSelector] = useState(false);
  const [emailError, setEmailError] = useState('');
  // About Me starts COLLAPSED on every state, and stays open once opened
  // (user, 2026-08-20). Collapsed-not-absent is the whole point: the caret is
  // the signal that more is shareable, while four job-shaped fields on landing
  // would stop someone who just wants a photo and a hometown. Persisted so the
  // choice survives a revisit — a UI preference, not exchanged data, so it
  // rides the same direct-read pattern as phoneVerified above.

  const [aboutMeExpanded, setAboutMeExpanded] = useState(
    () => localStorage.getItem(ABOUT_ME_EXPANDED_KEY) === 'true'
  );

  const toggleAboutMe = () => {
    const next = !aboutMeExpanded;
    setAboutMeExpanded(next);
    localStorage.setItem(ABOUT_ME_EXPANDED_KEY, String(next));
  };
  const phoneVerified = localStorage.getItem('phone_verified') === 'true';

  // Simple email validation
  const validateEmail = (email) => {
    if (!email) return true; // Empty is okay
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle input changes for non-contact fields (name, about-me section)
  const handleChange = (field, value) => {
    onProfileChange({
      ...profile,
      [field]: value,
    });
  };

  // Handle contact field changes from ContactFields component. Takes ONE patch
  // (see ContactFields' onChange docs) and spreads it, so a promotion that
  // moves two fields lands as a single change. The previous per-field form
  // rebuilt from the `profile` prop each call, so a two-call promotion read the
  // same stale record twice and the second call destroyed the first.
  const handleContactChange = (patch) => {
    // Validate email whenever the patch touches it
    if ('email' in patch) {
      const value = patch.email;
      if (value && !validateEmail(value)) {
        setEmailError('Please enter a valid email');
      } else {
        setEmailError('');
      }
    }
    onProfileChange({
      ...profile,
      ...patch,
    });
  };

  // Handle photo selection
  const handlePhotoSelect = (photoUrl) => {
    onProfileChange({
      ...profile,
      photoUrl,
    });
    setShowPhotoSelector(false);
  };

  // Handle photo click — straight to the OS picker; the stock-photo sheet is
  // dev-only, gated with the same expression the page uses elsewhere.
  const { openPhotoPicker, photoInput } = useProfilePhotoPicker(handlePhotoSelect);

  const handlePhotoClick = () => {
    if (onImageClick) {
      onImageClick();
    } else if (process.env.REACT_APP_BRANCH !== 'main') {
      setShowPhotoSelector(true);
    } else {
      openPhotoPicker();
    }
  };

  return (
    <>
      {/* Form Content - no scroll wrapper, parent handles scrolling */}
      <div
        style={{
          padding: `${SPACING.MD} 20px`,
          maxWidth: '600px',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        {/* Photo Selection - Circular button (hidden when photo is in header) */}
        {!hidePhoto && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '24px',
            }}
          >
            {/* NOTE: both ProfileScreen call sites pass hidePhoto, so this
                branch does not render today. The own-profile page's photo —
                and the frames' + badge — live in ProfileScreen's own header. */}
            <div
              onClick={handlePhotoClick}
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: profile.photoUrl ? 'transparent' : COLORS.LIGHT_GRAY,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundImage: profile.photoUrl ? `url(${profile.photoUrl})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                border: '2px solid #E5E5E5',
              }}
            >
              {!profile.photoUrl && <User size={44} weight="fill" color="#C7C7CC" />}
            </div>
          </div>
        )}

        {/* Name Field (hidden when name is shown in card header) */}
        {!hideName && (
          <div style={{ marginBottom: '16px', scrollMarginTop: '16px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                padding: '12px 16px',
                borderRadius: BORDER_RADIUS.PILL,
                border: '0.5px solid #E0E0E0',
                backgroundColor: 'white',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  marginRight: '8px', // Match ContactField spacing
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px', // Match ContactField icon slot
                  height: '36px',
                  flexShrink: 0,
                  color: COLORS.MEDIUM_GRAY,
                }}
              >
                <IdentificationBadge size={24} weight="regular" />
              </div>
              <input
                type="text"
                name="name"
                autoComplete="name"
                value={profile.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Name"
                autoCapitalize="words"
                autoCorrect="off"
                style={{
                  flex: 1,
                  border: 'none',
                  fontSize: '17px',
                  color: COLORS.DARK_GRAY,
                  backgroundColor: 'transparent',
                  outline: 'none',
                }}
              />
            </div>
          </div>
        )}

        {/* The sharing promise. Permanent, and deliberately quiet (user ruling
            2026-08-19): it's core to how the app works, and this is the page
            where you decide what's shareable at all — there is no
            per-connection "Shared with {Name}" ledger here to carry it. Light
            enough to read as a standing caption rather than a claim being
            pressed every visit. */}
        <div
          style={{
            fontSize: '14px',
            color: '#8E8E93',
            textAlign: 'center',
            marginTop: '-4px',
            marginBottom: '16px',
          }}
        >
          People only see what you share with them
        </div>

        {/* Contact Fields — the SAME treatment the detail pages use (user
            ruling 2026-08-19: "I want what people give and get to feel the
            same"). The separate full-width form look is retired. The add
            affordance stays inline here rather than below a Save button: this
            page has no Save-to-contacts for it to sit under. */}
        <ContactFields
          userData={profile}
          disabled={false}
          showLinks={false}
          compact={true}
          onChange={handleContactChange}
          starterFields={
            isCreationFlow
              ? // Anon shares one field during exchange. Complement it:
                // phone shared → show instagram, anything else → show phone
                [profile.phone ? 'instagram' : 'phone']
              : // Phone + Instagram always visible, empty or not — the frames
                // show "Add Instagram" as a standing row on a phone-only
                // profile. alwaysShowStarters was false here, so an unfilled
                // starter simply never rendered and the page showed one row.
                ['phone', 'instagram']
          }
          alwaysShowStarters={true}
          readOnlyFields={phoneVerified ? ['phone'] : []}
          addLabel="Add more socials"
          // Quiet on every tier (frames). addEmphasized was wired to
          // contactsOnly, so Simple tier — every user who reaches this page
          // from the People header — got the dark filled plus.
          addEmphasized={false}
        />

        {/* Email validation error */}
        {emailError && (
          <div
            style={{
              marginTop: '-12px',
              marginBottom: '16px',
              fontSize: '13px',
              color: '#FF3B30',
              marginLeft: '16px',
            }}
          >
            {emailError}
          </div>
        )}

        {/* Hometown — its own prominent section, separate from About Me.
            Default-on shareable field (see profile-hometown-sharing); placed
            front-and-center above About Me, no hard-required validation.
            Extra top/bottom space (40px) gives it its own zone, set apart
            from the dense contact list and About Me stacks. */}
        {/* City field — one block, two dressings (review 2026-08-09: the two
            mutually-exclusive copies were the second-copy drift class). Full:
            HOMETOWN header + neighborhood placeholder. SP page (6d): headerless
            and deliberately quiet (user mock 2026-08-08 — "would be great if
            people do it", not so emphasized); a filled city rides the card. */}
        <div style={{ marginTop: '40px', scrollMarginTop: '16px' }}>
          {/* No label — the MapPin inside CityAutocomplete carries identity the
              same way the phone and Instagram icons do in the rows above, so
              the whole top of the page stays label-free and labels begin only
              once About me is opened (user, 2026-08-20). This also retires the
              two mutually-exclusive dressings that lived here; one treatment,
              both tiers. */}
          <CityAutocomplete
            value={profile.currentCity}
            onChange={(text, place) =>
              // A pick also stores where it is; typed text is the label alone.
              // An earlier pick's point stays stored, but it carries its own
              // label, so it counts only while the hometown still says that.
              onProfileChange({
                ...profile,
                currentCity: text,
                ...(place && { currentCityPlace: place }),
              })
            }
            placeholder="Current city or town"
          />
        </div>

        {/* About Me — rendered on EVERY tier, collapsed by default. The old
            `!contactsOnly` strip removed the section entirely at Simple tier,
            so there was no caret and no hint the fields existed; the code
            comment for that 2026-08-08 minimum already read "staged back in
            later". This is that later (user, 2026-08-20). */}
        <div style={{ marginTop: '40px' }}>
          {/* Header is CENTRED, sentence case, caret stacked beneath — not the
              left-aligned SectionHeader with the caret pushed to the far edge
              that the rest of the app uses. Deliberate (frames, 2026-08-20):
              this is a threshold, not a settings row. Above it, quick things
              carried by icons; below it, the fuller stuff that needs naming. */}
          <div
            onClick={toggleAboutMe}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: aboutMeExpanded ? '20px' : '0',
              cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: '15px', color: '#8E8E93' }}>About me</div>
            <CaretDown
              size={18}
              weight="bold"
              color={COLORS.MEDIUM_GRAY}
              style={{
                transform: aboutMeExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          </div>

          {aboutMeExpanded && (
            <>
              {/* autoComplete is OFF on every field here (default in AboutField).
                  `organization` + name="company" put Org into iOS Safari's
                  Contacts-AutoFill group: the tap is intercepted for a
                  contact-card picker, and with no card configured nothing
                  happens — no focus, no keyboard. Title survived only because
                  `organization-title` is not in that group, which is exactly
                  why Title worked and Org did not across three device passes.
                  These are fields you fill in once about yourself; autofill
                  offers nothing here. */}
              <AboutField
                label="Title"
                name="title"
                value={profile.role || ''}
                onChange={(e) => handleChange('role', e.target.value)}
              />

              <AboutField
                label="Company"
                name="company"
                value={profile.organization || ''}
                onChange={(e) => handleChange('organization', e.target.value)}
              />

              {/* Copy kept as "What I'm up to", not "What I do" (user,
                    2026-08-20): Title and Org already carry the job. This is
                    the one field on the page that isn't a resume line, and it
                    is the only one that grows with its content. */}
              <AboutField
                label="What I'm up to"
                multiline
                value={profile.whatIDo || ''}
                onChange={(e) => handleChange('whatIDo', e.target.value)}
              />

              {/* NOTE: label asks "Places I like" while the stored key is
                    placesLived — the key is deliberately NOT renamed (tester
                    data rule: never rename existing keys). Logged in the plan. */}
              <AboutField
                label="Places I like"
                value={profile.placesLived || ''}
                onChange={(e) => handleChange('placesLived', e.target.value)}
              />
            </>
          )}
        </div>
      </div>

      {photoInput}
      {/* Photo Selection Overlay — dev only (see handlePhotoClick) */}
      {showPhotoSelector && (
        <PhotoSelectionOverlay
          currentPhotoUrl={profile.photoUrl}
          onSelect={handlePhotoSelect}
          onClose={() => setShowPhotoSelector(false)}
        />
      )}
    </>
  );
};

export default ProfileForm;
